import axios from 'axios'
import epsClient, { getUrl } from 'enrollment-platform/api/eps.client'
import type { AvailablePlanYearsResponseDto } from './dto/available-plan-years-response.dto'
import type { FetchPlanRequestDto } from './dto/fetch-plan-request.dto'
import type { PlanManagementTaskDto } from './dto/plan-management-task.dto'
import type { PlanManagementTasksListRequestDto } from './dto/plan-management-tasks-list-request.dto'
import type { PlanManagementTasksListResponseDto } from './dto/plan-management-tasks-list-response.dto'
export const getPlanManagementTasks = async (
  params: PlanManagementTasksListRequestDto
): Promise<{ rows: PlanManagementTaskDto[]; total: number }> => {
  const { data } = await epsClient.get<PlanManagementTasksListResponseDto>(
    '/api/v1/plan-management-v2/tasks',
    {
      params: {
        page: params.page,
        limit: params.limit,
        sort: params.sort,
        order: params.order,
        ...(params.status ? { status: params.status } : {}),
      },
    }
  )
  return { rows: data.data ?? [], total: data.meta?.total ?? 0 }
}

export const getPlanManagementTaskById = async (
  taskId: string
): Promise<PlanManagementTaskDto> => {
  const { data } = await epsClient.get<PlanManagementTaskDto>(
    `/api/v1/plan-management-v2/tasks/${taskId}`
  )
  return data
}

export const fetchPlanSnapshot = async (
  body: FetchPlanRequestDto
): Promise<PlanManagementTaskDto> => {
  const { data } = await epsClient.post<PlanManagementTaskDto>(
    '/api/v1/plan-management-v2/tasks/fetch-plan',
    body
  )
  return data
}

export const getAvailablePlanYears = async (): Promise<number[]> => {
  const { data } = await epsClient.get<AvailablePlanYearsResponseDto>(
    '/api/v1/plan-management-v2/tasks/available-year'
  )
  return data.planYears
}

// ---------------------------------------------------------------------------
// Compare API
// ---------------------------------------------------------------------------

export type NewCompareDataType =
  | 'LOCATION'
  | 'SMOKER_RATES'
  | 'PLAN_BENEFITS'
  | 'PLANS_AND_RATES'
  | 'SERVICE_AREAS'

export type CompareStatus = 'NEW' | 'MODIFIED' | 'UNCHANGED'

export interface CompareRowDto {
  naturalKeyHash: string
  status: CompareStatus
  stagingData: Record<string, unknown> | null
  liveData: Record<string, unknown> | null
  overrideParams: Record<string, unknown> | null
  overrideParamSources?: Record<string, string> | null
  overrideStatus?: 'DRAFT' | 'APPROVED' | null
}

export interface CompareResponseDto {
  items: CompareRowDto[]
  total: number
  page: number
  limit: number
}

export interface TypeSummaryDto {
  new: number
  deleted: number
  modified: number
  unchanged: number
  total: number
}

export interface CompareSummaryResponseDto {
  location: TypeSummaryDto
  smokerRates: TypeSummaryDto
  planBenefits: TypeSummaryDto
  plansAndRates: TypeSummaryDto
  serviceAreas: TypeSummaryDto
}

export async function getCompare(params: {
  taskId: string
  dataType: NewCompareDataType
  statuses?: CompareStatus[]
  states?: string[]
  locationSearch?: string
  issuerId?: string
  hiosPlanId?: string
  overrideStatus?: string
  sort?: string
  order?: 'asc' | 'desc'
  page?: number
  limit?: number
}): Promise<CompareResponseDto> {
  const { data } = await epsClient.get<CompareResponseDto>(
    `/api/v1/plan-management-v2/tasks/${params.taskId}/review/compare`,
    {
      params: {
        dataType: params.dataType,
        ...(params.statuses?.length
          ? { statuses: params.statuses.join(',') }
          : {}),
        ...(params.page !== undefined ? { page: params.page } : {}),
        ...(params.limit !== undefined ? { limit: params.limit } : {}),
        ...(params.states?.length ? { states: params.states } : {}),
        ...(params.locationSearch
          ? { locationSearch: params.locationSearch }
          : {}),
        ...(params.issuerId ? { issuerId: params.issuerId } : {}),
        ...(params.hiosPlanId ? { hiosPlanId: params.hiosPlanId } : {}),
        ...(params.overrideStatus
          ? { overrideStatus: params.overrideStatus }
          : {}),
        ...(params.sort ? { sort: params.sort } : {}),
        ...(params.order ? { order: params.order } : {}),
      },
    }
  )
  return data
}

export interface CompareCountsDto {
  new: number
  modified: number
  deleted: number
  unchanged: number
  total: number
}

export function createCancelToken() {
  return axios.CancelToken.source()
}

export function isRequestCancelled(err: unknown): boolean {
  return axios.isCancel(err)
}

export async function getCompareCounts(params: {
  taskId: string
  dataType: NewCompareDataType
  states?: string[]
  locationSearch?: string
  issuerId?: string
  hiosPlanId?: string
  cancelToken?: ReturnType<typeof axios.CancelToken.source>['token']
}): Promise<CompareCountsDto> {
  const { data } = await epsClient.get<CompareCountsDto>(
    `/api/v1/plan-management-v2/tasks/${params.taskId}/review/compare/counts`,
    {
      params: {
        dataType: params.dataType,
        ...(params.states?.length ? { states: params.states } : {}),
        ...(params.locationSearch
          ? { locationSearch: params.locationSearch }
          : {}),
        ...(params.issuerId ? { issuerId: params.issuerId } : {}),
        ...(params.hiosPlanId ? { hiosPlanId: params.hiosPlanId } : {}),
      },
      cancelToken: params.cancelToken,
    }
  )
  return data
}

export async function getCompareSummary(
  taskId: string
): Promise<CompareSummaryResponseDto> {
  const { data } = await epsClient.get<CompareSummaryResponseDto>(
    `/api/v1/plan-management-v2/tasks/${taskId}/review/compare/summary`
  )
  return data
}

// ---------------------------------------------------------------------------
// Deleted data
// ---------------------------------------------------------------------------

export type DeletedDataType = 'PLANS_AND_RATES' | 'PLAN_BENEFITS'

export interface DeletedRowDto {
  naturalKeyHash: string
  status: 'DELETED'
  stagingData: null
  liveData: Record<string, unknown> | null
  overrideParams: null
  overrideParamSources: null
  overrideStatus: null
}

export interface DeletedDataResponseDto {
  items: DeletedRowDto[]
  total: number
  page: number
  limit: number
}

export async function getDeletedData(params: {
  taskId: string
  dataType: DeletedDataType
  states?: string[]
  issuerId?: string
  hiosPlanId?: string
  page?: number
  limit?: number
}): Promise<DeletedDataResponseDto> {
  const { data } = await epsClient.get<DeletedDataResponseDto>(
    `/api/v1/plan-management-v2/tasks/${params.taskId}/review/deleted-data`,
    {
      params: {
        dataType: params.dataType,
        ...(params.page !== undefined ? { page: params.page } : {}),
        ...(params.limit !== undefined ? { limit: params.limit } : {}),
        ...(params.states?.length ? { states: params.states } : {}),
        ...(params.issuerId ? { issuerId: params.issuerId } : {}),
        ...(params.hiosPlanId ? { hiosPlanId: params.hiosPlanId } : {}),
      },
    }
  )
  return data
}

// ---------------------------------------------------------------------------
// CSV streaming export
// ---------------------------------------------------------------------------

export function getDataTypeCsvExportUrl(
  taskId: string,
  dataType: NewCompareDataType,
  opts: {
    statuses?: CompareStatus[]
    states?: string[]
    locationSearch?: string
    issuerId?: string
    hiosPlanId?: string
  }
): string {
  const params = new URLSearchParams({ dataType })
  if (opts.statuses?.length) params.set('statuses', opts.statuses.join(','))
  if (opts.states?.length) {
    for (const s of opts.states) params.append('states[]', s)
  }
  if (opts.locationSearch) params.set('locationSearch', opts.locationSearch)
  if (opts.issuerId) params.set('issuerId', opts.issuerId)
  if (opts.hiosPlanId) params.set('hiosPlanId', opts.hiosPlanId)
  return `${getUrl()}/api/v1/plan-management-v2/tasks/${taskId}/review/data-type-report/csv/export?${params}`
}

// ---------------------------------------------------------------------------
// Plan Benefit Import
// ---------------------------------------------------------------------------

export interface ImportNewBenefitsResponseDto {
  totalRows: number
  created: number
  updated: number
  skipped: number
  errors: { row: number; message: string }[]
}

export async function importNewPlanBenefits(
  taskId: string,
  file: File
): Promise<ImportNewBenefitsResponseDto> {
  const formData = new FormData()
  formData.append('file', file)
  const { data } = await epsClient.post<ImportNewBenefitsResponseDto>(
    `/api/v1/plan-management-v2/tasks/${taskId}/review/plan-benefits/import`,
    formData
  )
  return data
}

// ---------------------------------------------------------------------------
// Plan Benefits Export / Import (live table)
// ---------------------------------------------------------------------------

function extractFilename(disposition: unknown, fallback: string): string {
  if (typeof disposition !== 'string') return fallback
  const match = /filename="?([^"]+)"?/.exec(disposition)
  return match?.[1] ?? fallback
}

export async function getPlanBenefitYears(): Promise<number[]> {
  const { data } = await epsClient.get<{ years: number[] }>(
    '/api/v1/plan-management-v2/plan-benefits/years'
  )
  return data.years
}

export async function getPlanBenefitHiosPlanIds(
  year: number,
  states: string[]
): Promise<string[]> {
  const { data } = await epsClient.get<{ hiosPlanIds: string[] }>(
    '/api/v1/plan-management-v2/plan-benefits/hios-plan-ids',
    { params: { year, ...(states.length ? { states } : {}) } }
  )
  return data.hiosPlanIds
}

export async function downloadPlanBenefitsExcel(params: {
  year: number
  states: string[]
  hiosPlanIds: string[]
}): Promise<void> {
  const response = await epsClient.get(
    '/api/v1/plan-management-v2/plan-benefits/export',
    {
      params: {
        year: params.year,
        ...(params.states.length ? { states: params.states } : {}),
        ...(params.hiosPlanIds.length
          ? { hiosPlanIds: params.hiosPlanIds }
          : {}),
      },
      responseType: 'blob',
    }
  )
  const stateLabel = params.states.length
    ? params.states.join('-')
    : 'ALL-STATES'
  const filename = extractFilename(
    response.headers?.['content-disposition'],
    `PlanBenefits-${stateLabel}-${params.year}.csv`
  )

  const url = window.URL.createObjectURL(new Blob([response.data]))
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

export interface PlanBenefitsImportRowErrorDto {
  row: number
  message: string
}

export interface ImportPlanBenefitsResponseDto {
  success: boolean
  totalRows: number
  updated: number
  skipped: number
  rowErrors: number
  errors: PlanBenefitsImportRowErrorDto[]
  // Set only when an unexpected error was caught mid-import (distinct from a row-validation failure).
  errorMessage: string | null
}

// ---------------------------------------------------------------------------
// Plan Benefits Import — async: presign -> PUT to S3 -> start job -> poll
// ---------------------------------------------------------------------------

export async function presignPlanBenefitsImport(
  originalFilename: string
): Promise<{ folder: string; uploadUrl: string }> {
  const { data } = await epsClient.post<{ folder: string; uploadUrl: string }>(
    '/api/v1/plan-management-v2/plan-benefits/import/presigned-upload-url',
    { originalFilename }
  )
  return data
}

export async function startPlanBenefitsImport(
  folder: string,
  originalFilename: string
): Promise<{ jobId: string }> {
  const { data } = await epsClient.post<{ jobId: string }>(
    '/api/v1/plan-management-v2/plan-benefits/import',
    { folder, originalFilename }
  )
  return data
}

// Raw pg-boss job row from GET /jobs/:id — only the fields this feature reads.
export interface PlanBenefitsImportJobDto {
  id: string
  state:
    | 'created'
    | 'active'
    | 'completed'
    | 'failed'
    | 'retry'
    | 'cancelled'
    | 'expired'
  output: ImportPlanBenefitsResponseDto | null
}

export async function getPlanBenefitsImportJob(
  jobId: string
): Promise<PlanBenefitsImportJobDto> {
  const { data } = await epsClient.get<PlanBenefitsImportJobDto>(
    `/jobs/${jobId}`
  )
  return data
}

export interface ActivePlanBenefitsImportJobDto {
  jobId: string | null
  originalFilename: string | null
}

export async function getActivePlanBenefitsImportJob(): Promise<ActivePlanBenefitsImportJobDto> {
  const { data } = await epsClient.get<ActivePlanBenefitsImportJobDto>(
    '/api/v1/plan-management-v2/plan-benefits/import/active-job'
  )
  return data
}

// ---------------------------------------------------------------------------
// Plan Benefits Upload Logs (activity log)
// ---------------------------------------------------------------------------

export interface PlanBenefitUploadLogS3Dto {
  folder: string
  originalFile: string
  updateSql: string | null
  errors: string | null
  preUpdateBackup: string | null
  diff: string | null
}

export interface PlanBenefitUploadLogAdditionalDataDto {
  success: boolean
  originalFilename: string
  totalRows: number
  updated: number
  skipped: number
  rowErrors: number
  years: number[]
  hiosPlanIdsFilter?: string[] // legacy — older entries stored the full array
  hiosPlanIdsCount?: number
  errorMessage?: string | null
  s3: PlanBenefitUploadLogS3Dto
}

export interface PlanBenefitUploadLogDto {
  id: number
  timestamp: string
  actorName: string
  actorRole?: string
  description: string
  // additionalData is freeform JSON on the backend — treat every field as
  // possibly absent since older log entries may predate a field being added
  // or renamed.
  additionalData?: Partial<PlanBenefitUploadLogAdditionalDataDto>
}

const PLAN_BENEFIT_UPLOAD_CATEGORY = 'PLAN_MANAGEMENT_PLAN_BENEFIT_UPLOAD'
const PLAN_BENEFIT_UPLOAD_ACTION = 'Plan Benefits CSV Import'

export async function getPlanBenefitUploadLogs(params: {
  page: number
  limit: number
}): Promise<{ rows: PlanBenefitUploadLogDto[]; total: number }> {
  const { data } = await epsClient.get<{
    data: PlanBenefitUploadLogDto[]
    meta: { page: number; limit: number; total: number; totalPages: number }
  }>('/activity-log', {
    params: {
      categories: [PLAN_BENEFIT_UPLOAD_CATEGORY],
      action: [PLAN_BENEFIT_UPLOAD_ACTION],
      // Omit sort/order — the backend's ActivityLogQueryDto.sort validator
      // (@IsEnum on a PickType-derived class, not an actual enum) rejects any
      // non-empty value with a 422. The repository already defaults to
      // timestamp/desc when these are left off, which is what we want anyway.
      page: params.page,
      limit: params.limit,
    },
  })
  return { rows: data.data ?? [], total: data.meta?.total ?? 0 }
}

export async function getPlanBenefitUploadLogDownloadUrl(
  key: string
): Promise<string> {
  const { data } = await epsClient.get<{ url: string }>(
    '/api/v1/plan-management-v2/plan-benefits/upload-logs/download-url',
    { params: { key } }
  )
  return data.url
}

// ---------------------------------------------------------------------------
// Task management
// ---------------------------------------------------------------------------

export async function deleteTask(taskId: string): Promise<void> {
  await epsClient.delete(`/api/v1/plan-management-v2/tasks/${taskId}`)
}

export function getAuthHeader(): Record<string, string> {
  const { idToken } = JSON.parse(localStorage.getItem('admin-token') || '{}')
  return idToken ? { Authorization: idToken } : {}
}
